import { useMemo } from "react";

import { Heading } from "@magnetic/heading";
import { Plus } from "@magnetic/icons";

import { HFlex, PageContent, VFlex } from "src/components";
import { Button } from "src/components/buttons";
import { EmptyContainer } from "src/components/emptyContainer";
import { ErrorNotification } from "src/components/errorNotification";
import { SearchWithResultCount } from "src/components/SearchWithResultCount/SearchWithResultCount";
import { BotEditDeleteActions } from "src/components/tables/columnConfigs/botActionsColumn";
import Table, { TableData } from "src/components/typedTable";
import { ApiError } from "src/core";
import { DateType } from "src/dataTypes";
import { LinkedAccount } from "src/gen/schema/bilge/api";
import { User as Bot } from "src/gen/schema/models/user";
import { useClientFilter } from "src/hooks/useClientFilter";

import { botTableColumns } from "./tableColumns";

interface BotUsersTableProps {
  readonly bots: Bot[];
  readonly bindings: LinkedAccount[];
  readonly loading: boolean;
  readonly error: ApiError | undefined;
  readonly onAddBot: () => void;
  readonly onEditBot: (bot: Bot) => void;
  readonly onDeleteBot: (bot: Bot) => void;
}

/**
 * Table component for displaying and managing bot users
 */
export const BotUsersTable = ({
  bots,
  bindings,
  loading,
  error,
  onAddBot,
  onEditBot,
  onDeleteBot
}: BotUsersTableProps) => {
  const {
    query,
    filtered: filteredBots,
    onSearchChange
  } = useClientFilter(bots, (bot) => bot.email || "");

  const botTableData: TableData<typeof botTableColumns>[] = useMemo(() => {
    if (!filteredBots.length) {
      return [];
    }
    return filteredBots.map((bot) => {
      const name = bot.email;
      const enabled = bot.enabled;
      const createdAt = bot.metadata?.createdAt
        ? new Date(Number(bot.metadata.createdAt))
        : undefined;

      const bindingCount = bindings.filter(
        (binding) => binding.userName === name
      ).length;

      const hasBindings = bindingCount > 0;
      const deleteTooltipMessage = hasBindings
        ? `There ${
            bindingCount === 1 ? "is" : "are"
          } ${bindingCount} active binding${
            bindingCount === 1 ? "" : "s"
          }. Unbind all bot services to delete.`
        : "";

      const actions: BotEditDeleteActions = {
        edit: {
          handleCallback: () => onEditBot(bot)
        },
        delete: {
          handleCallback: () => onDeleteBot(bot),
          disabled: hasBindings,
          tooltip: hasBindings ? deleteTooltipMessage : undefined
        }
      };

      return {
        name: name,
        bindings: String(bindingCount),
        userStatus: enabled,
        createdAt: createdAt ? new DateType(createdAt) : undefined,
        action: actions
      };
    });
  }, [filteredBots, bindings, onDeleteBot, onEditBot]);

  return (
    <PageContent
      classNames={["bot-table-container"]}
      titleSection={
        <VFlex>
          <ErrorNotification error={error} />
          <HFlex>
            <Heading>Bot users</Heading>
          </HFlex>
          <HFlex align="center" justify="space-between">
            <SearchWithResultCount
              searchTerm={query}
              onSearchChange={onSearchChange}
              placeholder="Search by name"
              resultCount={filteredBots.length}
              dataQa="search-bot-users"
            />
            <Button
              icon={<Plus />}
              name="add-bot-user"
              onClick={onAddBot}
              kind="primary"
            >
              Add bot user
            </Button>
          </HFlex>
        </VFlex>
      }
      emptyState={
        !loading &&
        bots.length === 0 && (
          <EmptyContainer
            text="Create a bot user before binding Hyperfabricbot to a chat room."
            title="Add bot user"
            fontSize="p2"
            iconName="robot"
          />
        )
      }
      loading={loading}
    >
      {bots.length > 0 && (
        <div className="table-scroll-container">
          <Table
            name="bot-users-table"
            className="bot-users-table"
            columns={botTableColumns}
            data={botTableData}
            stickyHeader={true}
            size="compact"
          />
        </div>
      )}
    </PageContent>
  );
};
